import { sanitize } from '@ben/common/dom';
import { Dialog, useDialogState } from 'ariakit/dialog';
import { isEmpty, isNumber } from 'lodash';
import type { ReactNode, RefObject } from 'react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import useResizeObserver from 'use-resize-observer/polyfilled';
import type { ITheme } from '../../../utils/theme';
import { TypographyVariant } from '../../../utils/theme';
import { Checkbox } from '../../checkbox/checkbox';
import type { IButtonProps } from '../../deprecated-button/button';
import Button, { ButtonVariant } from '../../deprecated-button/button';
import Icon, { type IconName } from '../../icon/icon';
import { IconScale } from '../../icon/types';
import InlineTextButton from '../../inline-text-button/inline-text-button';
import InputFeedback from '../../input-feedback';
import Typography from '../../typography/typography';

export type ModalRef =
  | {
      showModal: () => void;
      hideModal: () => void;
    }
  | undefined;

export enum ModalVariant {
  Default = 'default',
  Icon = 'icon',
  Banner = 'banner',
}

interface AgreementState {
  value: boolean;
  label: string;
}

export interface ModalProps {
  title?: string;
  description?: string;
  descriptionBold?: string;
  secondaryText?: string;
  agreements?: string[];
  agreementsErrorText?: string;
  secondaryVariant?: ButtonVariant;
  primaryText?: string;
  primaryVariant?: ButtonVariant;
  primaryProps?: Partial<IButtonProps>;
  tertiaryText?: string;
  tertiaryIcon?: IconName;
  onTertiaryClick?: () => void;
  onConfirm?: () => void;
  onClose?: () => void;
  icon?: IconName;
  iconColor?: string;
  iconBackgroundColor?: string;
  banner?: string;
  variant?: ModalVariant;
  height?: string;
  width?: string;
  isOpen?: boolean;
  children?: ReactNode;
  className?: string;
}

export const Modal = forwardRef<ModalRef, ModalProps>(
  (
    {
      title,
      description,
      descriptionBold,
      secondaryText,
      agreements,
      agreementsErrorText,
      secondaryVariant = ButtonVariant.secondary,
      primaryText,
      primaryVariant = ButtonVariant.primary,
      primaryProps,
      tertiaryText,
      tertiaryIcon,
      onTertiaryClick,
      onConfirm,
      onClose,
      icon,
      iconColor,
      iconBackgroundColor,
      banner,
      height,
      variant = ModalVariant.Default,
      width = '490px',
      isOpen,
      children,
      className,
    }: ModalProps,
    ref
  ) => {
    const dialog = useDialogState();
    const theme = useTheme();
    const { t } = useTranslation();

    const agreementsErrorId = useId();
    const agreementsCheckboxRef = useRef<Record<string, HTMLInputElement>>({});

    const titleId = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const { height: containerHeight } = useResizeObserver<HTMLDivElement>({
      ref: containerRef as RefObject<HTMLDivElement>,
    });
    const initialAgreementsData: string[] = agreements || [];
    const agreementsDataWithDefaultValue = initialAgreementsData.map(
      (agreement) => ({
        label: agreement,
        value: false,
      })
    );

    const [agreementsData, setAgreementsData] = useState<AgreementState[]>(
      agreementsDataWithDefaultValue
    );
    const [agreementsHasError, setAgreementsHasError] = useState(false);

    const hasUncheckedAgreement = useMemo(
      () => agreementsData.some((checkbox) => !checkbox.value),
      [agreementsData]
    );
    const isSingleAgreement = useMemo(
      () => agreements?.length === 1,
      [agreements]
    );

    useEffect(() => {
      if (isEmpty(agreementsData) && !isEmpty(agreementsDataWithDefaultValue)) {
        setAgreementsData(agreementsDataWithDefaultValue);
      }
      if (!isEmpty(agreementsData) && isEmpty(agreementsDataWithDefaultValue)) {
        setAgreementsData([]);
      }
    }, [agreements, agreementsData, agreementsDataWithDefaultValue]);

    const closeModal = useCallback(() => {
      if (!isEmpty(agreements)) {
        setAgreementsHasError(false);
        setAgreementsData(agreementsDataWithDefaultValue);
      }
      dialog.hide();
    }, [agreements, agreementsDataWithDefaultValue, dialog]);

    // Open and close if using the isOpen prop
    useEffect(() => {
      if (isOpen === true) dialog.show();
      if (isOpen === false) dialog.hide();
    }, [isOpen, dialog]);

    useImperativeHandle(ref, () => {
      return {
        showModal: () => {
          dialog.show();
        },
        hideModal: () => {
          closeModal();
        },
      };
    }, [closeModal, dialog]);

    const handleConfirmation = useCallback(() => {
      if (hasUncheckedAgreement) {
        setAgreementsHasError(true);

        const firstUncheckedCheckboxIndex = agreementsData.findIndex(
          (checkbox) => !checkbox.value
        );
        if (isNumber(firstUncheckedCheckboxIndex)) {
          const checkboxRef =
            agreementsCheckboxRef.current[firstUncheckedCheckboxIndex];
          if (checkboxRef) {
            checkboxRef.focus();
          }
        }
        return;
      }

      setAgreementsHasError(false);
      onConfirm?.();
    }, [hasUncheckedAgreement, onConfirm, agreementsData]);

    const handleAgreementChange = useCallback(
      (agreementIndex: number, value: boolean) => {
        setAgreementsData((agreements) =>
          agreements.map((agreement, index) =>
            agreementIndex === index ? { ...agreement, value } : agreement
          )
        );
      },
      [setAgreementsData]
    );

    // Getting if the container has a scrollbar with a ResizeObserver
    // in case of browser resizing or content change
    const hasScrollbar = useMemo(() => {
      const { scrollHeight, clientHeight } = containerRef.current || {};
      return scrollHeight && clientHeight ? scrollHeight > clientHeight : false;

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerHeight]);

    return (
      <StyledDialog
        state={dialog}
        tabIndex={0}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        <Wrapper $height={height} $width={width} className={className}>
          {banner && variant === ModalVariant.Banner && (
            <BannerImage src={banner} />
          )}
          <Container ref={containerRef}>
            {icon && variant === ModalVariant.Icon && (
              <IconBackground $backgroundColor={iconBackgroundColor}>
                <Icon
                  name={icon}
                  scale={IconScale.XL}
                  color={iconColor ?? theme.palette.error.main}
                />
              </IconBackground>
            )}
            {title && (
              <TitleTypography
                variant={TypographyVariant.h2}
                dangerouslySetInnerHTML={{
                  __html: sanitize(title),
                }}
                id={titleId}
              />
            )}
            {description && (
              <DescriptionTypography
                variant={TypographyVariant.bodyLg1}
                dangerouslySetInnerHTML={{
                  __html: sanitize(description),
                }}
              />
            )}
            {descriptionBold && (
              <DescriptionBoldTypography variant={TypographyVariant.bodyLg3}>
                {descriptionBold}
              </DescriptionBoldTypography>
            )}
            {children}

            {agreementsData?.length > 0 && (
              <>
                <AgreementsContentContainer data-testid="group-checkbox-wrapper">
                  {agreementsData?.map((agreement, index) => (
                    <StyledCheckbox
                      $isSingleAgreement={isSingleAgreement}
                      key={index}
                      value={agreement.value}
                      label={agreement.label}
                      onChange={() =>
                        handleAgreementChange(index, !agreement.value)
                      }
                      typographyVariant={TypographyVariant.bodyMd1}
                      hasError={agreementsHasError && !agreement?.value}
                      aria-describedby={
                        agreementsHasError && !agreement.value
                          ? agreementsErrorId
                          : undefined
                      }
                      ref={(ref) => {
                        agreementsCheckboxRef.current[index] =
                          ref as HTMLInputElement;
                      }}
                    />
                  ))}
                </AgreementsContentContainer>
                {agreementsHasError && hasUncheckedAgreement && (
                  <InputErrorWrapper>
                    <InputFeedback
                      errorMessage={
                        agreementsErrorText ??
                        t('phrases.acceptAllConditionsToContinue', {
                          defaultValue:
                            'Please, read and accept all conditions to continue.',
                        })
                      }
                      id={agreementsErrorId}
                    />
                  </InputErrorWrapper>
                )}
              </>
            )}
          </Container>
          {(secondaryText || primaryText) && (
            <ButtonContainer
              $hasScrollbar={!!hasScrollbar}
              $hasTertiaryText={!!tertiaryText}
            >
              {secondaryText && (
                <Button
                  variant={secondaryVariant}
                  label={secondaryText}
                  onClick={() => {
                    onClose?.();
                    closeModal();
                  }}
                  minWidth="187px"
                />
              )}
              {primaryText && (
                <StyledButton
                  variant={primaryVariant}
                  label={primaryText}
                  onClick={handleConfirmation}
                  minWidth="208px"
                  {...primaryProps}
                />
              )}
            </ButtonContainer>
          )}
          {tertiaryText && (
            <StyledInlineTextButton
              label={tertiaryText}
              onClick={onTertiaryClick}
              iconName={tertiaryIcon}
              isRightIcon
            />
          )}
        </Wrapper>
      </StyledDialog>
    );
  }
);

Modal.displayName = 'Modal';

export default memo(Modal);

const StyledDialog = styled(Dialog)`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 6000;
    background: ${theme.palette.black(0.5)};
    display: flex;
    justify-content: center;
    align-items: center;
  `}
`;

const AgreementsContentContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: ${theme.spacing(3)};
    gap: ${theme.spacing(1.5)};
    text-align: left;
    padding: ${theme.spacing(2.5)};
    background-color: ${theme.palette.neutral[40]};
    align-items: flex-start;
  `}
`;

const StyledCheckbox = styled(Checkbox)`
  ${({ $isSingleAgreement }: { $isSingleAgreement: boolean }) => css`
    align-items: ${$isSingleAgreement ? 'flex-start' : 'center'};
  `}
`;

const InputErrorWrapper = styled.div`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(1.5)};
  `}
`;

const Container = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing(5)};
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-x: auto;
    max-height: 100%;
    width: 100%;
    @media ${theme.mediaQueries.xs}, ${theme.mediaQueries.sm} {
      padding: ${theme.spacing(5, 2)};
    }
  `}
`;

const Wrapper = styled.div`
  ${({
    theme,
    $height,
    $width,
  }: {
    theme: ITheme;
    $height?: string;
    $width?: string;
  }) => css`
    background-color: ${theme.palette.neutral['00']};
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    ${$width
      ? css`
          width: ${$width};
        `
      : ''}
    ${$height
      ? css`
          height: ${$height};
        `
      : css`
          max-height: 85vh;
        `}
    .checkbox-agreement {
      align-items: flex-start;
    }
  `}
`;

const IconBackground = styled.div`
  ${({
    theme,
    $backgroundColor,
  }: {
    theme: ITheme;
    $backgroundColor?: string;
  }) => css`
    background-color: ${$backgroundColor ?? theme.palette.warning.bg};
    width: 80px;
    min-width: 80px;
    height: 80px;
    min-height: 80px;
    border-radius: ${theme.borderRadius.full};
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: ${theme.spacing(5)};
    display: inline-flex;
  `}
`;

const TitleTypography = styled(Typography)`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing(1)};
  `}
`;
const DescriptionTypography = styled(Typography)`
  white-space: pre-line;
`;

const DescriptionBoldTypography = styled(Typography)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(1)};
  `}
`;

const StyledInlineTextButton = styled(InlineTextButton)`
  ${({ theme }) => css`
    padding-bottom: ${theme.spacing(5)};
  `}
`;

const ButtonContainer = styled.div`
  ${({
    theme,
    $hasScrollbar,
    $hasTertiaryText,
  }: {
    theme: ITheme;
    $hasScrollbar: boolean;
    $hasTertiaryText: boolean;
  }) => css`
    padding: ${$hasScrollbar || $hasTertiaryText
      ? theme.spacing(3, 5)
      : theme.spacing(5)};
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(3)};
    justify-content: center;
    @media ${theme.mediaQueries.xs}, ${theme.mediaQueries.sm} {
      padding: ${theme.spacing(5, 2)};
    }
    ${$hasScrollbar
      ? css`
          border-top: 1px solid ${theme.palette.neutral[60]};
        `
      : css`
          padding-top: 0px;
        `}
  `}
`;

const BannerImage = styled.img`
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  width: 100%;
  height: 150px;
  object-fit: cover;
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
`;
